import React, { Component } from "react";
import { Box, Typography, Button, Paper, Stack, Divider } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";

/**
 * Error boundary component to catch and display React errors
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ errorInfo });

    // Report to monitoring service if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    // If there's no error, render children normally
    if (!hasError) {
      return children;
    }

    // If a custom fallback component is provided, use it
    if (fallback) {
      return fallback(error, errorInfo, this.handleReset);
    }

    // Default error UI
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 600,
            width: "100%",
            borderRadius: 2,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <ErrorOutline color="error" sx={{ fontSize: 60 }} />

            <Typography variant="h5" component="h2" align="center" gutterBottom>
              Something went wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" align="center">
              We encountered an unexpected error. Please try refreshing the page
              or contact support if the problem persists.
            </Typography>

            {error && (
              <Typography
                variant="body2"
                color="error"
                sx={{
                  p: 2,
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  borderRadius: 1,
                  width: "100%",
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {error.toString()}
              </Typography>
            )}

            <Divider sx={{ width: "100%" }} />

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={this.handleReset}
                startIcon={<Refresh />}
              >
                Try Again
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }
}

/**
 * Function component wrapper for ErrorBoundary
 *
 * @param {Object} props Component properties
 * @param {ReactNode} props.children Child components
 * @param {Function} props.fallback Custom fallback component
 * @param {Function} props.onError Error handler callback
 * @returns {JSX.Element} Error boundary component
 */
export default function ErrorBoundaryWrapper(props) {
  return <ErrorBoundary {...props} />;
}

/**
 * Error fallback component for use with React error boundaries
 *
 * @param {Object} props Component properties
 * @param {Error} props.error Error object
 * @param {string} props.resetErrorBoundary Function to reset error boundary
 * @returns {JSX.Element} Error fallback UI
 */
export function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <Box
      role="alert"
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <ErrorOutline color="error" sx={{ fontSize: 40 }} />
      <Typography variant="h6">Something went wrong</Typography>
      <Typography variant="body2" color="text.secondary">
        {error?.message || "An unexpected error occurred"}
      </Typography>
      <Button
        variant="contained"
        onClick={resetErrorBoundary}
        startIcon={<Refresh />}
      >
        Try again
      </Button>
    </Box>
  );
}
